import React, { useState } from "react";
import { AiFillEye } from "react-icons/ai";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import { apiPost } from "../../utils/apiFetch";
import apiPath from "../../utils/pathObj";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const EmployeeList = ({ jobGroup, handleCandidateView, handleCompareSelected, user, onBulkSelectDone }) => {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkError, setBulkError] = useState(null);

  const getMatchBadgeColor = (score) => {
    if (score >= 80) return "bg-green-100 text-green-800";
    if (score >= 60) return "bg-yellow-100 text-yellow-800";
    if (score >= 40) return "bg-orange-100 text-orange-800";
    return "bg-red-100 text-red-800";
  };

  const toggleSelect = (candidateId) => {
    setSelectedIds((prev) =>
      prev.includes(candidateId) ? prev.filter((id) => id !== candidateId) : [...prev, candidateId]
    );
  };

  const toggleSelectAll = () => {
    const allIds = (jobGroup.candidates || []).map((c) => c.employee._id);
    const allSelected = allIds.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : allIds);
  };

  const selectedCandidates = (jobGroup.candidates || []).filter((c) =>
    selectedIds.includes(c.employee._id)
  );

  const handleBulkSelectForInterview = async () => {
    if (selectedIds.length === 0 || bulkLoading) return;
    setBulkLoading(true);
    setBulkError(null);
    try {
      const result = await apiPost(apiPath.bulkSelectForInterview, {
        employeeIDs: selectedIds,
        jobID: jobGroup.jobId
      });

      if (result?.status === 200 && result?.data?.success) {
        const { successCount, failedCount } = result.data.results;
        if (typeof onBulkSelectDone === "function") {
          onBulkSelectDone({ successCount, failedCount, jobGroup });
        }
        setSelectedIds([]);
      } else {
        setBulkError(t("ACTION_FAILED"));
      }
    } catch (err) {
      console.error("Bulk select error:", err);
      setBulkError(t("ACTION_FAILED"));
    } finally {
      setBulkLoading(false);
    }
  };

  const allIds = (jobGroup.candidates || []).map((c) => c.employee._id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));

  return (
    <div className="">
      {(selectedCandidates.length >= 1) && (
        <div className="mb-3 flex items-center justify-between bg-blue-50 border border-blue-200 px-4 py-2 flex-wrap gap-2">
          <span className="text-sm text-blue-800">
            {selectedCandidates.length} {t("CANDIDATES_SELECTED")}
          </span>
          <div className="flex items-center gap-2">
            {selectedCandidates.length >= 2 && (
              <button
                type="button"
                onClick={() => handleCompareSelected(selectedCandidates, jobGroup)}
                className="text-xs px-3 py-1.5 rounded bg-white border border-LightBlue text-LightBlue hover:bg-LightBlue hover:text-white"
              >
                {t("COMPARE_SELECTED")}
              </button>
            )}
            <button
              type="button"
              disabled={bulkLoading}
              onClick={handleBulkSelectForInterview}
              className="text-xs px-3 py-1.5 rounded bg-LightBlue text-white hover:bg-DarkBlue disabled:opacity-50 flex items-center gap-1"
            >
              {bulkLoading && <AiOutlineLoading3Quarters className="animate-spin w-3 h-3" />}
              {t("SELECT_ALL_FOR_INTERVIEW")}
            </button>
          </div>
        </div>
      )}

      {bulkError && (
        <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {bulkError}
        </div>
      )}

      <div className="overflow-x-auto relative">
        <table className="w-full text-xs text-left text-gray-600">
          <thead className="text-xs text-gray-900 border border-[#E1E6EE] bg-[#E1E6EE]">
            <tr>
              <th scope="col" className="py-3 px-4">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 accent-LightBlue cursor-pointer"
                  title={t("SELECT_ALL")}
                />
              </th>
              <th scope="col" className="py-3 px-6">#</th>
              <th scope="col" className="py-3 px-6">{t("EMPLOYEE_NAME")}</th>
              <th scope="col" className="py-3 px-6">{t("EMAIL")}</th>
              <th scope="col" className="py-3 px-6">{t("LOCATION")}</th>
              <th scope="col" className="py-3 px-6">{t("MATCH_SCORE")}</th>
              <th scope="col" className="py-3 px-6">{t("MATCHED_SKILLS")}</th>
              <th scope="col" className="py-3 px-6 text-center">{t("O_ACTION")}</th>
            </tr>
          </thead>
          <tbody>
            {jobGroup.candidates && jobGroup.candidates.map((candidate, i) => (
              <tr key={i} className="bg-white border-b hover:bg-gray-50">
                <td className="py-4 px-4">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(candidate.employee._id)}
                    onChange={() => toggleSelect(candidate.employee._id)}
                    className="w-4 h-4 accent-LightBlue cursor-pointer"
                  />
                </td>
                <td className="py-4 px-6 border-r">{i + 1}</td>
                <td className="py-4 px-6 border-r font-medium">{candidate.employee.name}</td>
                <td className="py-4 px-6 border-r">{candidate?.employee?.email}</td>
                <td className="py-4 px-6 border-r">
                  {candidate?.employee?.location?.city}{", "}{candidate?.employee?.location?.state}{", "}{candidate?.employee?.location?.country}
                </td>
                <td className="py-4 px-6 border-r">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getMatchBadgeColor(candidate.matchScore)}`}>
                    {candidate.matchScore}%
                  </span>
                </td>
                <td className="py-4 px-6 border-r">
                  <div className="flex flex-wrap gap-1">
                    {candidate.matchedSkills.length > 0 ? (
                      candidate.matchedSkills?.slice(0, 1).map((skill, idx) => (
                        <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">
                          {skill}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-500">{t("NO_MATCHED_SKILLS")}</span>
                    )}
                    {candidate.matchedSkills?.length > 1 && (
                      <span className="text-xs text-gray-500">+{candidate.matchedSkills.length - 1} more</span>
                    )}
                  </div>
                </td>
                <td className="py-4 px-6 border-l">
                  <div className="flex justify-center">
                    <button
                      onClick={() => handleCandidateView(candidate, jobGroup)}
                      className="p-2 hover:bg-gray-100 rounded-full"
                      title={t("VIEW_DETAILS")}
                    >
                      <AiFillEye className="w-5 h-5 text-gray-600 cursor-pointer" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {isEmpty(jobGroup.candidates) ? (
              <tr className="bg-white text-center border-b">
                <td className="py-4 px-6" colSpan={8}>{t("NO_CANDIDATES_FOR_THIS_JOB")}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EmployeeList;